/**
 * The component displays survey indicators from the server.
 * When compoment was rendered, we will call server to load data.
 */
import PropTypes from "prop-types";

import React, { Component } from "react";
import { INDICATOR_EDITOR } from "../../../common/constant";

import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";

import Button from "react-bootstrap/Button";
import { FormattedMessage, injectIntl } from "react-intl";
import SurveyUtils from "../../../utils/SurveyUtils";
import FundingAreaContainer from "../fundingArea/FundingAreaContainer";

import { faPencilAlt, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Visibility from "../../../component/common/VisibleContainer";
import {
    INDICATOR_FUNDING_AREA,
    INDICATOR_ID,
    INDICATOR_LONG,
    INDICATOR_PROGRAM,
    INDICATOR_SHORT
} from "../../../ducks/indicatorAdmin";
import StandardView from "../../common/StandardView";
import IndicatorPanel from "./IndicatorPanel";

class IndicatorAdmin extends Component {
  constructor(props) {
    super(props);
    this.row = this.row.bind(this);
    this.onNewIndicator = this.onNewIndicator.bind(this);
    this.close = this.close.bind(this);
    this.confirm = this.confirm.bind(this);
    this.onSortColumn = this.onSortColumn.bind(this);
    this.loadIndicator = this.loadIndicator.bind(this);
  }

  render() {
    const {
      indicatorList,
      isNew,
      showModal,
      filterSelectedArea,
      indicatorId,
      indicatorShort,
      indicatorName,
      fundingArea,
      program,
      fieldErrorMessages,
      changeProperty,
      sortTableIndicatorBy,
      sortTableTemplate
    } = this.props;

    return (
      <StandardView
        id="indicator"
        titleKey="indicatorList.title"
        actions={[
          {
            labelKey: "indicatorList.new.indicator",
            onClick: this.onNewIndicator,
            roles: [INDICATOR_EDITOR],
            icon: <FontAwesomeIcon icon={faPlus} className="me-2" />
          }
        ]}
      >
        <Modal show={showModal}>
          <Modal.Header>
            <Modal.Title>
              <FormattedMessage id={isNew ? "indicatorList.new.indicator" : "indicatorList.edit.indicator"} />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <IndicatorPanel
              isNew={isNew}
              indicatorShort={indicatorShort}
              program={program}
              fieldErrorMessages={fieldErrorMessages}
              changeProperty={changeProperty}
              indicatorName={indicatorName}
              indicatorId={indicatorId}
              fundingArea={fundingArea}
              filterSelectedArea={filterSelectedArea}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={this.close} variant="secondary">
              <FormattedMessage id="app.common.Cancel" />
            </Button>
            <Button onClick={this.confirm}>
              <FormattedMessage id={isNew ? "app.common.Create" : "app.common.Apply"} />
            </Button>
          </Modal.Footer>
        </Modal>
        <FundingAreaContainer
          target="indicatorsModalDialog"
          filterSelected={() => this.loadIndicatorListFilter()}
          preventProjectChoice={true}
          preventProgramInstance={true}
        />
        <Table className="mt-4" responsive striped hover>
          <thead>
            <tr>
              <th className={SurveyUtils.addClassForTableHeader(INDICATOR_ID, sortTableIndicatorBy, sortTableTemplate)}>
                <Button variant="link" onClick={() => this.onSortColumn(INDICATOR_ID, sortTableIndicatorBy)}>
                  <FormattedMessage id="indicatorList.ID" />
                  {SurveyUtils.sortIcon(INDICATOR_ID, sortTableIndicatorBy, sortTableTemplate)}
                </Button>
              </th>
              <th
                className={SurveyUtils.addClassForTableHeader(INDICATOR_SHORT, sortTableIndicatorBy, sortTableTemplate)}
              >
                <Button variant="link" onClick={() => this.onSortColumn(INDICATOR_SHORT, sortTableIndicatorBy)}>
                  <FormattedMessage id="indicatorList.IndicatorShortVersion" />
                  {SurveyUtils.sortIcon(INDICATOR_SHORT, sortTableIndicatorBy, sortTableTemplate)}
                </Button>
              </th>
              <th
                className={SurveyUtils.addClassForTableHeader(INDICATOR_LONG, sortTableIndicatorBy, sortTableTemplate)}
              >
                <Button variant="link" onClick={() => this.onSortColumn(INDICATOR_LONG, sortTableIndicatorBy)}>
                  <FormattedMessage id="indicatorList.IndicatorLongVersion" />
                  {SurveyUtils.sortIcon(INDICATOR_LONG, sortTableIndicatorBy, sortTableTemplate)}
                </Button>
              </th>
              <th
                className={SurveyUtils.addClassForTableHeader(
                  INDICATOR_FUNDING_AREA,
                  sortTableIndicatorBy,
                  sortTableTemplate
                )}
              >
                <Button variant="link" onClick={() => this.onSortColumn(INDICATOR_FUNDING_AREA, sortTableIndicatorBy)}>
                  <FormattedMessage id="indicatorList.fundingArea" />
                  {SurveyUtils.sortIcon(INDICATOR_FUNDING_AREA, sortTableIndicatorBy, sortTableTemplate)}
                </Button>
              </th>
              <th
                className={SurveyUtils.addClassForTableHeader(
                  INDICATOR_PROGRAM,
                  sortTableIndicatorBy,
                  sortTableTemplate
                )}
              >
                <Button variant="link" onClick={() => this.onSortColumn(INDICATOR_PROGRAM, sortTableIndicatorBy)}>
                  <FormattedMessage id="indicatorList.program" />
                  {SurveyUtils.sortIcon(INDICATOR_PROGRAM, sortTableIndicatorBy, sortTableTemplate)}
                </Button>
              </th>
              <Visibility roles={[INDICATOR_EDITOR]} as="th" className="actions">
                <FormattedMessage id="app.common.title.actions" />
              </Visibility>
            </tr>
          </thead>
          <tbody>{indicatorList.map(this.row)}</tbody>
        </Table>
      </StandardView>
    );
  }

  /**
   * A row renderer
   */
  row(indicator, i) {
    const { intl, sortTableIndicatorBy } = this.props;
    return (
      <tr key={i}>
        <td className={SurveyUtils.addClassForTableColumns(INDICATOR_ID, sortTableIndicatorBy)}>{indicator.title}</td>
        <td className={SurveyUtils.addClassForTableColumns(INDICATOR_SHORT, sortTableIndicatorBy)}>
          {indicator.shortName}
        </td>
        <td className={SurveyUtils.addClassForTableColumns(INDICATOR_LONG, sortTableIndicatorBy)}>
          {indicator.longName}
        </td>
        <td className={SurveyUtils.addClassForTableColumns(INDICATOR_FUNDING_AREA, sortTableIndicatorBy)}>
          {indicator.fundingArea ? indicator.fundingArea.name : ""}
        </td>
        <td className={SurveyUtils.addClassForTableColumns(INDICATOR_PROGRAM, sortTableIndicatorBy)}>
          {indicator.program ? indicator.program.name : <FormattedMessage id="indicatorList.programs.all" />}
        </td>
        <Visibility roles={[INDICATOR_EDITOR]} as="td" className="actions">
          <Button
            size="sm"
            variant="link"
            title={intl.formatMessage({ id: "indicatorList.tooltip.edit" })}
            onClick={() => this.loadIndicator(indicator)}
          >
            <FontAwesomeIcon icon={faPencilAlt} />
          </Button>
        </Visibility>
      </tr>
    );
  }

  /**
   * Before render get all indicators
   * */
  componentDidMount() {
    this.props.loadIndicatorListFilter();
  }

  /**
   *
   *Calls 'ducks' function, that will load the indicator data from the server
   */
  loadIndicator(indicator) {
    this.props.loadIndicator(indicator);
  }

  /**
   * Load all indicators
   * */
  loadIndicatorListFilter() {
    this.props.filterSelected();
  }

  /**
   *
   * triggers new_indicator_event
   */
  onNewIndicator() {
    this.props.newIndicator();
  }

  /**
   *
   * triggers close modal dialog
   */
  close() {
    const { closeModalDialog } = this.props;
    closeModalDialog();
  }

  /**
   *
   * triggers submit indicator
   */
  confirm() {
    const {
      submitModalDialog,
      isNew,
      indicatorId,
      indicatorShort,
      indicatorName,
      saveIndicator,
      fundingArea,
      program,
      identifier
    } = this.props;
    if (isNew) {
      submitModalDialog();
    } else {
      saveIndicator(indicatorId, indicatorShort, indicatorName, fundingArea, program, identifier);
    }
  }

  /**
   *
   * Sorting table data
   */
  onSortColumn(columnType) {
    const { sortTableTemplate, filterSelected } = this.props;
    // trigger sort function from ducks
    filterSelected(columnType, sortTableTemplate);
  }
}

IndicatorAdmin.propTypes = {
  indicatorList: PropTypes.array,
  newIndicator: PropTypes.func,
  saveIndicator: PropTypes.func,
  showModal: PropTypes.bool,
  loadIndicatorListFilter: PropTypes.func,
  closeModalDialog: PropTypes.func,
  submitModalDialog: PropTypes.func,
  indicatorName: PropTypes.string,
  indicatorShort: PropTypes.string,
  fieldErrorMessages: PropTypes.func,
  changeProperty: PropTypes.func,
  fundingArea: PropTypes.object,
  isNew: PropTypes.bool,
  program: PropTypes.object,
  indicatorId: PropTypes.string,
  filterSelected: PropTypes.func,
  filterSelectedArea: PropTypes.func,
  loadIndicator: PropTypes.func,
  identifier: PropTypes.string,
  sortColumn: PropTypes.func,
  sortTableIndicatorBy: PropTypes.string,
  sortTableTemplate: PropTypes.bool,
  shortName: PropTypes.string,
  longName: PropTypes.string
};

export default injectIntl(IndicatorAdmin);
